import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserRound } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  StatusBadge,
  StatusStepper,
  buttonVariants,
} from "@/design-system/components/ui";
import { LogInAsAction } from "@/components/impersonation/LogInAsAction";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import {
  portalAccountsApi,
  type PortalAccountActivityDto,
  type PortalAccountDetailDto,
  type TemporaryPasswordResult,
} from "@/api/portalAccounts";
import { portalAbsorptionKeys } from "@/hooks/portalAbsorption/portalAbsorptionKeys";
import { AccountStatusModal } from "@/pages/admin/portal/AccountStatusModal";

// Portal-absorption §2.3 (WS-G) — the shared staff "Portal Account" TAB.
// Mounted by per-record detail pages (party/vendor/broker/…) for in-place
// account management: lifecycle hero, one contextual primary action, the
// account summary, intent-grouped admin actions, and the activity timeline.
// The invite affordance is record-type-specific, so the host owns that
// mutation and passes it in via the `invite` prop.

export interface PortalAccountTabProps {
  /** Linked PortalUser id; null/undefined renders the no-account state. */
  portalUserId: string | null | undefined;
  /** Send/resend the record-type-specific portal invitation (host owns the mutation + toasts). */
  invite?: { send: () => void; pending: boolean };
  emptyHint?: string;
}

// The staff SPA and the portal SPA share one origin; every portal account
// type signs in through the same login route (verified in PortalApp.tsx).
const PORTAL_LOGIN_PATH = "/portal/login";

type StatusAction = "disable" | "enable" | "archive" | "unarchive";

const STATUS_SUCCESS: Record<StatusAction, string> = {
  disable: "Account disabled.",
  enable: "Account re-enabled.",
  archive: "Account archived.",
  unarchive: "Account unarchived.",
};

// Mirrors the PortalAccountDetailPage STATUS_MODAL copy — disable/enable
// require a reason (min 10 chars, enforced by AccountStatusModal);
// archive/unarchive take an optional note.
const STATUS_MODAL: Record<
  StatusAction,
  {
    title: string;
    description: string;
    label: string;
    placeholder: string;
    reasonRequired: boolean;
    confirmLabel: string;
    destructive: boolean;
  }
> = {
  disable: {
    title: "Disable account?",
    description:
      "The user will be signed out of the portal and blocked from logging in. In-flight packages are frozen, not cancelled.",
    label: "Reason for disabling",
    placeholder: "Explain why this account is being disabled. Min. 10 characters.",
    reasonRequired: true,
    confirmLabel: "DISABLE ACCOUNT",
    destructive: true,
  },
  enable: {
    title: "Re-enable account?",
    description: "The account returns to normal; in-flight packages resume as before.",
    label: "Reason for re-enabling",
    placeholder: "Explain why this account is being re-enabled. Min. 10 characters.",
    reasonRequired: true,
    confirmLabel: "RE-ENABLE",
    destructive: false,
  },
  archive: {
    title: "Archive account?",
    description: "Hides the account from the default list. It can be unarchived at any time.",
    label: "Reason",
    placeholder: "Optional note.",
    reasonRequired: false,
    confirmLabel: "ARCHIVE",
    destructive: false,
  },
  unarchive: {
    title: "Unarchive account?",
    description: "Returns the account to the default list.",
    label: "Reason",
    placeholder: "Optional note.",
    reasonRequired: false,
    confirmLabel: "UNARCHIVE",
    destructive: false,
  },
};

function isLockedNow(a: PortalAccountDetailDto): boolean {
  return Boolean(a.lockoutEndUtc && new Date(a.lockoutEndUtc).getTime() > Date.now());
}

export function PortalAccountTab({
  portalUserId,
  invite,
  emptyHint = "No portal account is linked to this record yet.",
}: PortalAccountTabProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const { dateTime, relative } = useDateFormatter();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Portal.ManageAccounts");

  const id = portalUserId ?? "";

  const detailQuery = useQuery({
    queryKey: portalAbsorptionKeys.accountDetail(id),
    queryFn: () => portalAccountsApi.get(id),
    enabled: Boolean(portalUserId),
  });
  const activityQuery = useQuery({
    queryKey: portalAbsorptionKeys.accountActivity(id),
    queryFn: () => portalAccountsApi.getActivity(id),
    enabled: Boolean(portalUserId),
  });

  // Every admin action mutates the account row and/or appends to its audit
  // trail — refresh both reads together (stale-after-save convention).
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.accountDetail(id) });
    void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.accountActivity(id) });
  };

  const [statusAction, setStatusAction] = useState<StatusAction | null>(null);
  const [confirmAction, setConfirmAction] = useState<null | "force" | "signout">(null);
  const [tempPasswordOpen, setTempPasswordOpen] = useState(false);

  const statusMut = useMutation({
    mutationFn: (vars: { action: StatusAction; reason: string }) => {
      switch (vars.action) {
        case "disable":
          return portalAccountsApi.disable(id, vars.reason);
        case "enable":
          return portalAccountsApi.enable(id, vars.reason);
        case "archive":
          return portalAccountsApi.archive(id, vars.reason || undefined);
        case "unarchive":
          return portalAccountsApi.unarchive(id, vars.reason || undefined);
      }
    },
    onSuccess: (_data, vars) => {
      toast.success(STATUS_SUCCESS[vars.action]);
      setStatusAction(null);
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  const unlockMut = useMutation({
    mutationFn: () => portalAccountsApi.unlock(id),
    onSuccess: () => {
      toast.success("Account unlocked.");
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  const forcePasswordChangeMut = useMutation({
    mutationFn: () => portalAccountsApi.forcePasswordChange(id),
    onSuccess: () => {
      toast.success("The user must change their password at next sign-in.");
      setConfirmAction(null);
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  const signOutEverywhereMut = useMutation({
    mutationFn: () => portalAccountsApi.signOutEverywhere(id),
    onSuccess: () => {
      toast.success("All portal sessions for this account were ended.");
      setConfirmAction(null);
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  const resendPasswordLinkMut = useMutation({
    mutationFn: () => portalAccountsApi.resendPasswordLink(id),
    onSuccess: (data) => {
      if (data.delivered) toast.success(data.message);
      else toast.error(data.message);
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  // The generated password is rendered once inside the dialog, never toasted.
  const tempPasswordMut = useMutation({
    mutationFn: () => portalAccountsApi.setTemporaryPassword(id),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  const copyLoginLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${PORTAL_LOGIN_PATH}`);
      toast.success("Login link copied.");
    } catch {
      toast.error("Couldn't copy the link — copy it from the address bar of the portal login page.");
    }
  };

  // ---- No-account state -------------------------------------------------
  if (!portalUserId) {
    return (
      <EmptyState title="No portal account" description={emptyHint} icon={UserRound}>
        {invite && (
          <div className="mt-space-4">
            <Button
              type="button"
              onClick={invite.send}
              disabled={invite.pending}
              loading={invite.pending}
              data-testid="portal-account-tab-send-invite"
            >
              Send portal invite
            </Button>
          </div>
        )}
      </EmptyState>
    );
  }

  if (detailQuery.isLoading) {
    return <p className="text-small text-gray-mid">Loading account…</p>;
  }

  const a = detailQuery.data;
  if (detailQuery.isError || !a) {
    return (
      <p className="text-small text-gray-mid">
        Could not load the linked portal account.
      </p>
    );
  }

  // ---- Lifecycle derivation (mirrors PortalAccountDetailPage) -----------
  // invited = the account exists; confirmed = emailConfirmed; active =
  // confirmed AND has logged in at least once. Disabled/Archived are
  // additive overrides beside the stepper (Archived visually wins).
  const confirmed = a.emailConfirmed === true;
  const active = confirmed && a.lastLoginUtc != null;
  const currentIndex = active ? 2 : confirmed ? 1 : 0;
  const locked = isLockedNow(a);
  const credentialActionsAvailable = !a.isDisabled && !a.isArchived;

  // ONE contextual primary action under the stepper — first match wins.
  let primary: { label: string; onClick: () => void; pending: boolean } | null = null;
  if (a.isArchived) {
    if (canManage)
      primary = {
        label: "Unarchive",
        onClick: () => setStatusAction("unarchive"),
        pending: statusMut.isPending,
      };
  } else if (a.isDisabled) {
    if (canManage)
      primary = {
        label: "Re-enable",
        onClick: () => setStatusAction("enable"),
        pending: statusMut.isPending,
      };
  } else if (locked) {
    if (canManage)
      primary = {
        label: "Unlock",
        onClick: () => unlockMut.mutate(),
        pending: unlockMut.isPending,
      };
  } else if (!confirmed) {
    if (invite)
      primary = {
        label: "Resend invite",
        onClick: invite.send,
        pending: invite.pending,
      };
  } else if (a.lastLoginUtc == null) {
    if (canManage)
      primary = {
        label: "Resend password link",
        onClick: () => resendPasswordLinkMut.mutate(),
        pending: resendPasswordLinkMut.isPending,
      };
  }

  // One muted diagnostic sentence — the single most relevant fact.
  let diagnostic: string;
  if (locked && a.lockoutEndUtc) {
    diagnostic = `Locked until ${dateTime(a.lockoutEndUtc)}${
      a.accessFailedCount ? ` after ${a.accessFailedCount} failed attempts` : ""
    }.`;
  } else if (!confirmed) {
    diagnostic = `Invited ${relative(a.createdAt)} — never logged in.`;
  } else if (a.mustChangePassword) {
    diagnostic = "Must change password at next login.";
  } else if (a.lastLoginUtc == null) {
    diagnostic = "Email confirmed — never logged in.";
  } else {
    diagnostic = `Last login ${relative(a.lastLoginUtc)}.`;
  }

  const summaryRows: Array<{ label: string; value: ReactNode }> = [
    {
      label: "Email",
      value: <span className="min-w-0 break-all font-mono">{a.email}</span>,
    },
    { label: "Account type", value: a.accountType },
    { label: "Created", value: dateTime(a.createdAt) },
    { label: "Last login", value: a.lastLoginUtc ? dateTime(a.lastLoginUtc) : "never" },
    { label: "First login", value: a.firstLoginAt ? dateTime(a.firstLoginAt) : "—" },
    {
      label: "Failed attempts",
      value: typeof a.accessFailedCount === "number" ? a.accessFailedCount : "—",
    },
  ];

  const activityRows = [...(activityQuery.data ?? [])].sort(
    (x, y) => new Date(y.occurredAtUtc).getTime() - new Date(x.occurredAtUtc).getTime(),
  );

  return (
    <div className="flex flex-col gap-space-4" data-testid="portal-account-tab">
      {/* Hero — lifecycle stepper + override badges + contextual primary. */}
      <div className="flex flex-col gap-space-2">
        <div className="flex flex-wrap items-center gap-space-3">
          <StatusStepper
            steps={[
              { key: "invited", label: "Invited", hint: "An account exists for this record." },
              { key: "confirmed", label: "Confirmed", hint: "The user confirmed their email." },
              { key: "active", label: "Active", hint: "The user has signed in to the portal." },
            ]}
            currentIndex={currentIndex}
            complete={active}
            label="Portal account lifecycle"
            data-testid="portal-account-tab-stepper"
          />
          {a.isArchived && <StatusBadge tone="muted">Archived</StatusBadge>}
          {a.isDisabled && <StatusBadge tone="danger">Disabled</StatusBadge>}
          {locked && <StatusBadge tone="warn">Locked</StatusBadge>}
        </div>
        <p className="text-small text-gray-mid" data-testid="portal-account-tab-diagnostic">
          {diagnostic}
        </p>
        {primary && (
          <div>
            <Button
              type="button"
              onClick={primary.onClick}
              disabled={primary.pending}
              loading={primary.pending}
              data-testid="portal-account-tab-primary-action"
            >
              {primary.label}
            </Button>
          </div>
        )}
      </div>

      {/* Account summary. */}
      <Card data-testid="portal-account-tab-summary">
        <CardHeader>
          <CardTitle>Account summary</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-space-4 gap-y-space-2 sm:grid-cols-2">
            {summaryRows.map((row) => (
              <div key={row.label} className="flex min-w-0 flex-col">
                <dt className="text-caption uppercase text-gray-mid">{row.label}</dt>
                <dd className="m-0 min-w-0 text-small text-onyx">{row.value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {/* Admin actions, grouped by intent. Self-gated on Portal.ManageAccounts. */}
      {canManage && (
        <Card data-testid="portal-account-tab-actions">
          <CardHeader>
            <CardTitle>Manage account</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-space-3">
            <ActionGroup label="Access">
              {invite && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={invite.send}
                  disabled={invite.pending}
                  loading={invite.pending}
                >
                  {confirmed ? "Resend invite" : "Send/Resend invite"}
                </Button>
              )}
              <Button type="button" variant="secondary" size="sm" onClick={() => void copyLoginLink()}>
                Copy login link
              </Button>
              {credentialActionsAvailable && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => resendPasswordLinkMut.mutate()}
                    disabled={resendPasswordLinkMut.isPending}
                    loading={resendPasswordLinkMut.isPending}
                  >
                    Resend password link
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setTempPasswordOpen(true)}
                  >
                    Set temporary password
                  </Button>
                </>
              )}
              {locked && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => unlockMut.mutate()}
                  disabled={unlockMut.isPending}
                  loading={unlockMut.isPending}
                  data-testid="portal-account-tab-unlock"
                >
                  Unlock
                </Button>
              )}
            </ActionGroup>

            <ActionGroup label="Account">
              {a.isDisabled ? (
                <Button type="button" variant="secondary" size="sm" onClick={() => setStatusAction("enable")}>
                  Re-enable
                </Button>
              ) : (
                <Button type="button" variant="secondary" size="sm" onClick={() => setStatusAction("disable")}>
                  Disable
                </Button>
              )}
              {a.isArchived ? (
                <Button type="button" variant="secondary" size="sm" onClick={() => setStatusAction("unarchive")}>
                  Unarchive
                </Button>
              ) : (
                <Button type="button" variant="secondary" size="sm" onClick={() => setStatusAction("archive")}>
                  Archive
                </Button>
              )}
            </ActionGroup>

            <ActionGroup label="Security">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setConfirmAction("force")}
              >
                Force password change
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setConfirmAction("signout")}
                data-testid="portal-account-tab-sign-out-everywhere"
              >
                Sign out everywhere
              </Button>
            </ActionGroup>

            <ActionGroup label="Support">
              <a
                href={PORTAL_LOGIN_PATH}
                target="_blank"
                rel="noopener"
                className={buttonVariants({ variant: "secondary", size: "sm" })}
              >
                Open portal
              </a>
              {/* Self-gating on Users.Impersonate + the ADMIN role — it renders
                  nothing for anyone else, so no host has to thread a flag in.
                  Opens THIS account's portal in a new tab; the admin's own staff
                  session here is untouched. Deliberately NOT gated on
                  `credentialActionsAvailable`: the siblings above hide themselves
                  for disabled/archived accounts because those actions would fail,
                  but impersonation does not — Decision Q5 impersonates them on
                  purpose (seeing what a disabled user sees is one of the reasons
                  this exists), so matching the siblings here would be a silent
                  regression. */}
              <LogInAsAction
                target={{
                  kind: "portal",
                  portalUserId: a.id,
                  label: a.fullName || a.email,
                  accountType: a.accountType,
                }}
              />
            </ActionGroup>
          </CardContent>
        </Card>
      )}

      {/* Activity timeline — newest first. */}
      <section className="flex flex-col gap-space-3" data-testid="portal-account-tab-activity">
        <h3 className="text-h5 font-semibold">Activity</h3>
        {activityQuery.isLoading ? (
          <p className="text-small text-gray-mid">Loading…</p>
        ) : activityRows.length === 0 ? (
          <p className="text-small text-gray-mid">No account activity yet.</p>
        ) : (
          <ul className="flex flex-col gap-space-2">
            {activityRows.map((r: PortalAccountActivityDto) => (
              <li
                key={r.id}
                className="flex flex-col gap-space-1 rounded border border-border-light p-space-3"
              >
                <div className="flex items-center justify-between gap-space-3">
                  <span className="text-small font-semibold text-onyx">{r.action}</span>
                  <span className="text-caption text-gray-mid">{dateTime(r.occurredAtUtc)}</span>
                </div>
                <div className="text-small text-onyx break-words">{r.description}</div>
                <div className="text-caption text-gray-mid">
                  {r.actorDisplayName ?? "Unknown user"}
                  {r.actorRoleCode ? ` · ${r.actorRoleCode}` : ""}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {statusAction && (
        <AccountStatusModal
          open
          {...STATUS_MODAL[statusAction]}
          pending={statusMut.isPending}
          onClose={() => setStatusAction(null)}
          onConfirm={(reason) => statusMut.mutate({ action: statusAction, reason })}
        />
      )}

      {confirmAction === "force" && (
        <ConfirmActionModal
          title="Force password change?"
          body="The user will be required to set a new password the next time they sign in to the portal."
          confirmLabel="Force password change"
          pending={forcePasswordChangeMut.isPending}
          onConfirm={() => forcePasswordChangeMut.mutate()}
          onClose={() => setConfirmAction(null)}
        />
      )}

      {confirmAction === "signout" && (
        <ConfirmActionModal
          title="Sign out everywhere?"
          body="Ends every active portal session for this account. The user can sign back in immediately with their current password."
          confirmLabel="Sign out everywhere"
          pending={signOutEverywhereMut.isPending}
          onConfirm={() => signOutEverywhereMut.mutate()}
          onClose={() => setConfirmAction(null)}
        />
      )}

      {tempPasswordOpen && (
        <SetTemporaryPasswordDialog
          pending={tempPasswordMut.isPending}
          result={tempPasswordMut.data ?? null}
          onGenerate={() => tempPasswordMut.mutate()}
          onClose={() => {
            setTempPasswordOpen(false);
            tempPasswordMut.reset();
          }}
        />
      )}
    </div>
  );
}

function ActionGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-space-1">
      <span className="text-caption uppercase text-gray-mid">{label}</span>
      <div className="flex flex-wrap items-center gap-space-2">{children}</div>
    </div>
  );
}

// Small local confirm dialog for the Security actions (mirrors the modal
// footer conventions used by AccountStatusModal).
function ConfirmActionModal({
  title,
  body,
  confirmLabel,
  pending,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      onClose={pending ? () => {} : onClose}
      dismissOnBackdropClick={false}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={pending}
            loading={pending}
            data-testid="portal-account-tab-confirm"
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-small text-onyx">{body}</p>
    </Modal>
  );
}

// Mirrors PortalAccountDetailPage's SetTemporaryPasswordDialog: the returned
// password is plaintext-once — rendered here with a copy button and a
// "shown only once" warning, never toasted or logged.
function SetTemporaryPasswordDialog({
  pending,
  result,
  onGenerate,
  onClose,
}: {
  pending: boolean;
  result: TemporaryPasswordResult | null;
  onGenerate: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      toast.success("Temporary password copied.");
    } catch {
      toast.error("Couldn't copy — select the field and copy it manually.");
    }
  };

  return (
    <Modal
      open
      onClose={pending ? () => {} : onClose}
      dismissOnBackdropClick={false}
      title="Set temporary password"
      description="Generate a one-time password to hand to the user directly."
      size="sm"
      footer={
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          {result ? "Done" : "Cancel"}
        </Button>
      }
    >
      <div className="flex flex-col gap-space-3">
        {result ? (
          <>
            <div
              className="rounded-md border border-status-warning bg-status-warning/10 p-space-3 text-small text-onyx"
              role="alert"
            >
              Shown once — copy it now. This password is not emailed; share it
              with the user directly.
            </div>
            <div>
              <label
                htmlFor="portal-account-tab-temp-password"
                className="block text-small text-gray-mid"
              >
                Temporary password
              </label>
              <div className="flex items-center gap-space-2">
                <Input
                  id="portal-account-tab-temp-password"
                  value={result.temporaryPassword}
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button type="button" variant="secondary" onClick={() => void copy()}>
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
            {result.mustChangeOnNextLogin && (
              <p className="text-caption text-gray-mid">
                The user will be required to change this password at next sign-in.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-small text-onyx">
              This sets a temporary password. Share it with the user directly —
              it will NOT be emailed. They will be required to change it at next
              sign-in. It is shown only once.
            </p>
            <div>
              <Button type="button" onClick={onGenerate} disabled={pending} loading={pending}>
                Generate temporary password
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
